import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faClipboard } from '@fortawesome/free-solid-svg-icons';
import { useNotification } from '../../context/NotificationContext';
import { NotificationType, Position } from '../../types/enums/notificationEnums';
import { motion, AnimatePresence } from 'framer-motion';
import Marquee from 'react-fast-marquee';

interface Coupon {
  id: number;
  code: string;
  description: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  min_cart_total: number;
  end_date: string;
}

const PromotionBanner: React.FC = () => {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);
  const { showNotification } = useNotification();
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [currentCouponIndex, setCurrentCouponIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        const response = await api.get('/coupons/showcase');
        if (response.data.success && response.data.data.length > 0) {
          setCoupons(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching promotion coupons:', error);
      }
    };

    fetchCoupons();
  }, []);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);

    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  useEffect(() => {
    if (coupons.length > 1) {
      const interval = setInterval(() => {
        setCurrentCouponIndex(prevIndex => (prevIndex + 1) % coupons.length);
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [coupons]);

  const handleDismiss = () => {
    setIsVisible(false);
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard
      .writeText(code)
      .then(() => {
        showNotification(
          t('Coupon code {{code}} copied to clipboard!', { code }),
          NotificationType.Success,
          Position.TopRight
        );
      })
      .catch(() => {
        showNotification(
          t('Failed to copy coupon code.'),
          NotificationType.Error,
          Position.TopRight
        );
      });
  };

  if (!isVisible || coupons.length === 0) {
    return null;
  }

  const currentCoupon = coupons[currentCouponIndex];
  const formattedEndDate = new Date(currentCoupon.end_date).toLocaleDateString();
  const discountText =
    currentCoupon.discount_type === 'percentage'
      ? `${currentCoupon.discount_value}%`
      : `${currentCoupon.discount_value}€`;

  const bannerAnimation = {
    initial: { opacity: 0, y: -10 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 },
    transition: { duration: 0.4 }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          {...bannerAnimation}
          className="bg-amber-100 py-2.5 text-center relative w-full dark:from-orange-500 dark:to-orange-600 dark:bg-gradient-to-r"
        >
          <div className="flex items-center justify-between px-4 gap-4">
            {/* Yazı kısmı */}
            <div className="flex-1 overflow-hidden">
              <Marquee gradient={false} speed={50} pauseOnHover={true}>
                <div className="flex items-center gap-x-2 text-sm text-gray-800 dark:text-white">
                  <span className="font-semibold whitespace-nowrap text-sm">
                    {t('Special Offer!')} {discountText} {t('off')}
                  </span>
                  <span className="hidden sm:inline mx-1 text-xs dark:text-white">•</span>
                  <span className="whitespace-nowrap text-xs">{t('Use code')}:</span>
                  <code className="font-mono text-sm font-bold bg-orange-500 bg-opacity-20 px-2 py-0.5 rounded mx-1 text-orange-700 dark:bg-gray-800 dark:text-orange-400">
                    {currentCoupon.code}
                  </code>
                  {currentCoupon.min_cart_total > 0 && currentCoupon.end_date && (
                    <span className="text-xs whitespace-nowrap hidden sm:inline">
                      {t('promotion_conditions', {
                        minTotal: currentCoupon.min_cart_total,
                        endDate: formattedEndDate
                      })}
                    </span>
                  )}
                  {currentCoupon.description && (
                    <span className="text-xs font-light text-gray-600 dark:text-gray-300 mr-10 sm:mr-12 md:mr-20">
                      ({currentCoupon.description})
                    </span>
                  )}
                </div>
              </Marquee>
            </div>

            {/* Butonlar */}
            <div className="flex items-center gap-2 shrink-0">
              {isMobile ? (
                <motion.button
                  onClick={() => handleCopyCode(currentCoupon.code)}
                  className="w-8 h-8 text-gray-500 hover:text-gray-700 flex items-center justify-center bg-white rounded-full shadow-sm dark:text-gray-100 dark:hover:text-zinc-300 dark:bg-gray-800"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FontAwesomeIcon icon={faClipboard} className="h-4 w-4" />
                </motion.button>
              ) : (
                <motion.button
                  onClick={() => handleCopyCode(currentCoupon.code)}
                  className="whitespace-nowrap bg-orange-500 hover:bg-orange-600 text-white px-3 py-1 text-sm font-semibold rounded-full transition-colors shadow-md"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {t('Copy code')}
                </motion.button>
              )}

              <button
                type="button"
                className="w-8 h-8 flex lg:mr-2 items-center justify-center text-gray-500 hover:text-gray-700 bg-white rounded-full shadow-sm dark:text-gray-100 dark:hover:text-zinc-300 dark:bg-gray-800"
                onClick={handleDismiss}
              >
                <span className="sr-only">{t('Dismiss')}</span>
                <FontAwesomeIcon icon={faXmark} className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PromotionBanner;
